import { Box, Dialog, Portal, Table, Text, VStack } from '@chakra-ui/react';
import { ReactComponent as DownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ExchangeIcon } from 'assets/icons/exchange-filled.svg';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { fixDecimal } from 'utils';

type ApproveModalType = {
  onClose: () => void;
  isOpen: boolean;
  loading: boolean;
  data: {
    to: string;
    from: string;
    rate: number;
    amount: number;
    destination_amount: number;
  };
  onButton: () => void;
};

const ApproveModal: React.FC<ApproveModalType> = ({
  onClose,
  isOpen,
  loading,
  data,
  onButton,
}: ApproveModalType) => {
  const { t } = useTranslation('component.exchange.modal');

  return (
    <Box display="flex" alignItems="center">
      <Dialog.Root
        size="xl"
        open={isOpen}
        placement="center"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps({ maxW: 'xl' })}>
              <ServiceDialogHeader>{t('confirm')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody py={6} px={4}>
                <VStack align="center" justify="flex-start" w="100%">
                  <VStack w="100%" align="center" justify="center" flex="auto">
                    <ExchangeIcon />
                    <Text fontSize="4xl" fontWeight="500" color="gray" py={2}>
                      {`${data.amount} ${data.from}`}
                    </Text>
                    <DownIcon />
                    <Text fontSize="4xl" fontWeight="500">
                      {`${fixDecimal({
                        value: data.destination_amount,
                        fractionDigits: 5,
                      })} ${data.to}`}
                    </Text>
                  </VStack>
                  <Table.ScrollArea w="100%" overflowX="auto">
                    <Table.Root variant="line">
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell textAlign="left">{t('from')}</Table.Cell>
                          <Table.Cell textAlign="right">{data.from}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell textAlign="left">{t('to')}</Table.Cell>
                          <Table.Cell textAlign="right">{data.to}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell textAlign="left">{t('rate')}</Table.Cell>
                          <Table.Cell textAlign="right">
                            {`1 ${data.from} = `}
                            {data.rate}
                            {` ${data.to}`}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <Button
                  w="100%"
                  variant="filled"
                  title={t('confirm')}
                  onClick={() => {
                    onButton();
                    onClose();
                  }}
                  isLoading={loading}
                  {...DIALOG_ACTION_BUTTON_PROPS}
                />
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default ApproveModal;
